import { useLoader } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { COURT_DIMENSIONS } from "./courtConstants";
import { isPointInPolygon, normalizeToWorldSpace } from "./courtUtils";

// Region names mapped to their correct indices based on actual SVG regions
const REGION_NAMES = [
	"Restricted Area", // Zone 0
	"Low Post / Low Block (Right)", // Zone 1
	"Low Post / Low Block (Left)", // Zone 2
	"High Post / Elbow (Left)", // Zone 3
	"Top of the Key", // Zone 4
	"Wing (Mid-Range) Left", // Zone 5
	"High Post / Elbow (Right)", // Zone 6
	"Wing (Mid-Range) Right", // Zone 7
	"Top of the Key 3", // Zone 8
	"Wing 3 (Left)", // Zone 9
	"Corner 3 (Left)", // Zone 10
	"Wing 3 (Right)", // Zone 11
	"Corner 3 (Right)", // Zone 12
	"Short Corner / Baseline", // Zone 13 - single zone for both baseline areas
];

export const RegionStatsProcessor = ({ shots, onStatsCalculated }) => {
	const svgData = useLoader(SVGLoader, "/img/heatmap/heatmap.svg");
	const shapes = useMemo(
		() => svgData.paths.flatMap((path) => path.toShapes(true)),
		[svgData],
	);

	// Transform SVG regions to world space once
	const worldSpaceRegions = useMemo(() => {
		if (!shapes || shapes.length === 0) return [];

		const scale = 0.0585;
		const position = new THREE.Vector3(
			COURT_DIMENSIONS.WIDTH / 2,
			0.02,
			-COURT_DIMENSIONS.HEIGHT / 2,
		);
		const rotation = new THREE.Euler(-Math.PI / 2, 0, Math.PI, "XYZ");

		// Create forward transformation matrix
		const matrix = new THREE.Matrix4();
		matrix.compose(
			position,
			new THREE.Quaternion().setFromEuler(rotation),
			new THREE.Vector3(scale, scale, scale),
		);

		// Transform each SVG region polygon to world space
		return shapes.map((shape) => {
			const svgPoints = shape.getPoints();
			return svgPoints.map((p) => {
				const localVec = new THREE.Vector3(p.x, p.y, 0);
				const worldVec = localVec.applyMatrix4(matrix);
				return { x: worldVec.x, y: worldVec.z };
			});
		});
	}, [shapes]);

	const processedData = useMemo(() => {
		if (!shapes || shapes.length === 0 || !shots || !svgData) {
			return { stats: [] };
		}

		const stats = new Array(shapes.length).fill(0).map((_, i) => {
			return {
				name: REGION_NAMES[i] || `Region ${i + 1}`,
				made: 0,
				total: 0,
				totalPoints: 0,
				shots: [], // Store shots for this zone
			};
		});

		shots.forEach((shot) => {
			if (!shot.location) return;

			// Normalize to world space (handles both old {x,y} and new {x,y,z} formats)
			const worldLocation = normalizeToWorldSpace(shot.location);
			const shotPoint = { x: worldLocation.x, y: worldLocation.z };

			// Check which region the shot belongs to
			for (
				let regionIndex = 0;
				regionIndex < worldSpaceRegions.length;
				regionIndex++
			) {
				const polygon = worldSpaceRegions[regionIndex];

				if (isPointInPolygon(shotPoint, polygon)) {
					stats[regionIndex].total++;
					stats[regionIndex].shots.push(shot);

					if (shot.field_goal_result === "score") {
						stats[regionIndex].made++;

						// Calculate points based on shot type
						let pointsPerShot = 2; // Default to 2 points
						if (shot.field_goal_position === "3PFG") {
							pointsPerShot = 3;
						} else if (shot.field_goal_position === "FT") {
							pointsPerShot = 1;
						}

						stats[regionIndex].totalPoints += pointsPerShot;
					}
					break; // A shot belongs to only one region
				}
			}
		});

		const calculatedStats = stats.map((stat) => ({
			...stat,
			percentage:
				stat.total > 0 ? Math.round((stat.made / stat.total) * 100) : 0,
		}));

		return { stats: calculatedStats };
	}, [shots, worldSpaceRegions, shapes, svgData]);

	useEffect(() => {
		onStatsCalculated(processedData);
	}, [processedData, onStatsCalculated]);

	return null;
};
